/**
 * Tahap pertama alur multi-mineral: mineral apa saja yang dicari di batuan ini.
 *
 * Sampai kini daftarnya hanya bisa dibaca. Tabelnya tercetak lengkap di layar
 * analisis — enam komponen, endpoint tiap alat, batas volumenya — dan di
 * bawahnya berdiri sebuah tombol bertuliskan "Add a component" yang tidak punya
 * satu baris penangan pun: ia tergambar, bisa ditekan, dan tidak melakukan
 * apa-apa. Sementara itu seluruh jalan untuk menyuntingnya sudah ada di server:
 * `PUT /api/mineral-models/{id}/` memeriksa modelnya lalu **menerbitkan versi
 * baru** alih-alih menimpa yang dirujuk solve lama.
 *
 * Yang khas pada tahap ini, dan yang membuatnya pantas berdiri sebagai tahap
 * dan bukan sebagai panel setelan: mengganti daftar mineral mengganti
 * pertanyaannya, bukan jawabannya. Sebuah solve tiga mineral dan sebuah solve
 * enam mineral atas log yang sama adalah dua tafsiran yang berbeda, bukan satu
 * tafsiran dengan angka yang digeser.
 */
import { useState } from "react";

import type { MineralComponent, MineralModel, ToolResponse } from "../types";
import { ColorPicker } from "./ColorPicker";
import { Button, Dropdown, Modal, NumberField, Notice, ParamCaption } from "./ui";

/**
 * Peran sebuah komponen di dalam persamaan non-linear.
 *
 * Bukan hiasan dan bukan tebakan: persamaan resistivitas dan ekskavasi neutron
 * perlu tahu volume mana yang pori, mana yang air terikat, dan mana yang
 * hidrokarbon. `petro/multilump.py` memang bisa menebaknya dari nama, dan
 * tebakan itu benar untuk keenam nama bawaan — tetapi sebuah mineral yang baru
 * saja diketik orang tidak punya nama yang pernah ditebak siapa pun.
 */
export const COMPONENT_ROLES = [
  { value: "matriks", label: "Matrix", hint: "quartz, calcite, dolomite" },
  { value: "lempung", label: "Clay", hint: "counted as Vsh" },
  { value: "air_terikat", label: "Bound water", hint: "clay-bound, not free pore" },
  { value: "air_bebas", label: "Free water", hint: "pore water" },
  { value: "hidrokarbon", label: "Hydrocarbon", hint: "pore fluid" },
];

/** Peran yang menempati ruang pori — `fluid` mengikutinya, tidak diketik sendiri. */
const FLUID_ROLES = new Set(["air_terikat", "air_bebas", "hidrokarbon"]);

/**
 * Peran yang dibaca dari namanya — cermin `ROLE_PATTERNS` di `petro/multilump.py`.
 *
 * Disalin ke sini bukan karena menyalin itu baik melainkan karena keadaannya
 * menuntutnya: model bawaan yang ditanam `seed_demo` menyimpan `role: null`
 * pada keenam komponennya dan menyerahkan penebakan kepada server. Sebuah
 * jendela yang tidak ikut menebak akan memajang air bebas sebagai "Matrix" —
 * salah di layar, dan sekali menu itu disentuh, salah pula di basis data.
 */
const ROLE_PATTERNS: [MineralComponent["role"], RegExp][] = [
  ["air_terikat", /terikat|bound|irreducible|clay\s*bound/],
  ["hidrokarbon", /hidrokarbon|hydrocarb|minyak|oil|gas|kondensat/],
  ["air_bebas", /air\s*bebas|free\s*water|air\b|water|brine|formasi/],
  ["lempung", /lempung|clay|shale|serpih|illit|smektit|kaolin|klorit/],
];

/**
 * Peran sebuah komponen: dari `role` bila ada, dari namanya bila tidak.
 *
 * Urutan yang sama persis dengan `guess_role` di server, termasuk jatuhnya ke
 * air bebas bagi komponen bertanda `fluid` yang namanya tidak dikenali.
 */
export function guessRole(component: MineralComponent): NonNullable<MineralComponent["role"]> {
  const declared = String(component.role ?? "").trim().toLowerCase();
  if (declared && ["matriks", "lempung", "air_terikat", "air_bebas", "hidrokarbon"].includes(declared)) {
    return declared as NonNullable<MineralComponent["role"]>;
  }
  const name = String(component.name ?? "").toLowerCase();
  for (const [role, pattern] of ROLE_PATTERNS) {
    if (pattern.test(name)) return role as NonNullable<MineralComponent["role"]>;
  }
  return component.fluid ? "air_bebas" : "matriks";
}

/**
 * Alat yang menuntut endpoint dari tiap komponen.
 *
 * Resistivitas tidak: ia mengikuti air-ganda atas porositas dan saturasi, dan
 * tidak pernah membaca endpoint mineral mana pun. Menuntut sebuah angka RT dari
 * kuarsa berarti meminta angka yang tidak akan pernah dibaca — dan kolom yang
 * begitu terbaca seperti angka yang menentukan sesuatu. Cermin
 * `ENDPOINT_FREE_EQUATIONS` di `petro/models.py`.
 */
const ENDPOINT_FREE = new Set(["resistivity"]);

/** Kolom endpoint yang perlu diisi, menurut alat yang sedang menyala. */
export function endpointColumns(tools: ToolResponse[]): string[] {
  return tools
    .filter((tool) => tool.enabled && !ENDPOINT_FREE.has(String(tool.equation ?? "linear")))
    .map((tool) => tool.name);
}

/** Satuan tiap endpoint, supaya kotaknya menyebutkan apa yang diketik di dalamnya. */
const ENDPOINT_UNITS: Record<string, string> = {
  RHOB: "g/cc",
  NPHI: "v/v",
  DTC: "us/ft",
  GR: "gAPI",
  PEF: "b/e",
};

/** Langkah kotak angkanya — densitas bergerak dalam perseratusan, GR dalam satuan. */
const ENDPOINT_STEPS: Record<string, number> = {
  RHOB: 0.01, NPHI: 0.01, DTC: 0.5, GR: 1, PEF: 0.01,
};

/** Komponen baru: matriks tanpa nama, dengan endpoint kuarsa sebagai titik berangkat. */
export function blankComponent(taken: string[]): MineralComponent {
  let name = "Mineral baru";
  for (let n = 2; taken.includes(name); n += 1) name = `Mineral baru ${n}`;
  return {
    name,
    color: "#b08968",
    role: "matriks",
    fluid: false,
    RHOB: 2.65,
    NPHI: 0,
    DTC: 55.5,
    GR: 20,
    PEF: 2.0,
  };
}

/**
 * Apa yang menghalangi model ini disimpan, atau "" bila tidak ada.
 *
 * Diperiksa di sini **dan** di server, dan itu bukan pengulangan yang sia-sia:
 * server yang memutuskan, sebab ia satu-satunya yang tidak bisa dilewati; layar
 * yang menyebutkannya lebih dulu, sebab sebuah tombol simpan yang memulangkan
 * 400 sesudah dua belas angka diketik tidak mengatakan angka mana yang salah
 * sampai jawabannya dibaca.
 */
export function componentProblem(
  components: MineralComponent[],
  columns: string[],
): string {
  if (components.length < 2) {
    return "A solve needs at least two components: one mineral cannot be separated from itself.";
  }
  const names = components.map((c) => (c.name ?? "").trim());
  if (names.some((name) => !name)) return "Every component needs a name.";
  if (new Set(names).size !== names.length) return "Component names must be unique.";
  for (const component of components) {
    for (const column of columns) {
      const value = component[column as keyof MineralComponent];
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return `${component.name} has no endpoint for ${column}.`;
      }
    }
    const lo = component.min;
    const hi = component.max;
    for (const [key, value] of [["min", lo], ["max", hi]] as const) {
      if (value !== undefined && value !== null && (value < 0 || value > 1)) {
        return `The ${key} of ${component.name} is a volume fraction between 0 and 1.`;
      }
    }
    if (lo !== undefined && hi !== undefined && lo !== null && hi !== null && lo > hi) {
      return `The lower bound of ${component.name} exceeds its upper bound.`;
    }
  }
  if (!components.some((c) => FLUID_ROLES.has(guessRole(c)))) {
    return "No component occupies the pore: give at least one of them a fluid role.";
  }
  return "";
}

/**
 * Daftar mineral sebagai jendelanya sendiri.
 *
 * Menyimpan menerbitkan versi baru modelnya — tiap solve lama tetap menunjuk
 * baris yang dipakainya — jadi tombolnya menyebut penerbitan dan bukan
 * penyimpanan.
 */
export function MineralComponentsDialog({
  model,
  busy = false,
  disabled = false,
  onClose,
  onSave,
}: {
  model: MineralModel;
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onSave: (components: MineralComponent[]) => void;
}) {
  /**
   * Perannya dituliskan saat jendela dibuka, bukan dibiarkan kosong.
   *
   * Model bawaan menyimpan `role: null` dan menyerahkan penebakan kepada
   * server tiap kali ia dipakai. Itu berjalan selama tidak ada yang
   * menyuntingnya — tetapi tebakan yang hidup di satu sisi saja adalah tebakan
   * yang cepat atau lambat dibaca berbeda oleh sisi yang lain, dan sebuah
   * jendela penyunting adalah tempat paling wajar untuk mengakhirinya. Yang
   * diterbitkan karena itu selalu membawa perannya sendiri.
   */
  const [draft, setDraft] = useState<MineralComponent[]>(() =>
    model.components.map((component) => {
      const role = guessRole(component);
      return { ...component, role, fluid: FLUID_ROLES.has(role) };
    }),
  );
  const columns = endpointColumns(model.tools);
  const problem = componentProblem(draft, columns);

  const edit = (index: number, patch: Partial<MineralComponent>) =>
    setDraft((current) =>
      current.map((component, i) => (i === index ? { ...component, ...patch } : component)),
    );

  return (
    <Modal
      title="1 · Mineral components"
      width={860}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled || Boolean(problem)}
            onClick={() => onSave(draft)}
          >
            {`Publish v${model.version + 1}`}
          </Button>
        </>
      }
    >
      <ParamCaption>Components</ParamCaption>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {draft.map((component, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 7,
              padding: "9px 10px",
              background: "var(--panel)",
              border: "1px solid var(--border-control)",
              borderRadius: "var(--radius-panel)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ColorPicker
                value={component.color}
                onChange={(color) => edit(index, { color })}
                label={`Colour of ${component.name}`}
                disabled={disabled}
                compact
              />
              <input
                value={component.name}
                aria-label={`Name of component ${index + 1}`}
                onChange={(event) => edit(index, { name: event.target.value })}
                disabled={disabled}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: 26,
                  padding: "0 8px",
                  background: "var(--panel-inset)",
                  border: "1px solid var(--line-strong)",
                  borderRadius: 5,
                  font: "500 11.5px var(--font-sans)",
                  color: "var(--ink)",
                }}
              />
              <Dropdown
                ariaLabel={`Role of ${component.name}`}
                value={guessRole(component)}
                options={COMPONENT_ROLES}
                // `fluid` mengikuti perannya alih-alih diketik terpisah: dua
                // medan yang menyatakan hal yang sama adalah dua medan yang
                // cepat atau lambat berselisih, dan yang membacanya di mesin
                // adalah perannya.
                onChange={(role) =>
                  edit(index, {
                    role: role as MineralComponent["role"],
                    fluid: FLUID_ROLES.has(role),
                  })
                }
                width={168}
                compact
                disabled={disabled}
              />
              <Button
                variant="ghost"
                style={{ height: 24 }}
                disabled={disabled || draft.length <= 2}
                title={
                  draft.length <= 2
                    ? "A solve needs at least two components."
                    : `Remove ${component.name}`
                }
                onClick={() =>
                  setDraft((current) => current.filter((_, i) => i !== index))
                }
              >
                Remove
              </Button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
                gap: 7,
              }}
            >
              {columns.map((column) => (
                <NumberField
                  key={column}
                  label={column}
                  unit={ENDPOINT_UNITS[column]}
                  value={Number(component[column as keyof MineralComponent] ?? 0)}
                  step={ENDPOINT_STEPS[column] ?? 0.01}
                  onChange={(value) => edit(index, { [column]: value } as Partial<MineralComponent>)}
                  disabled={disabled}
                />
              ))}
              <NumberField
                label="min V"
                value={Number(component.min ?? 0)}
                step={0.01}
                min={0}
                max={1}
                onChange={(value) => edit(index, { min: value })}
                disabled={disabled}
              />
              <NumberField
                label="max V"
                value={Number(component.max ?? 1)}
                step={0.01}
                min={0}
                max={1}
                onChange={(value) => edit(index, { max: value })}
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <Button
          variant="ghost"
          disabled={disabled}
          onClick={() =>
            setDraft((current) => [...current, blankComponent(current.map((c) => c.name))])
          }
        >
          Add a component
        </Button>
        <span style={{ flex: 1 }} />
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {`${draft.length} components · ${columns.length} endpoints each`}
        </span>
      </div>

      {problem ? (
        <Notice>{problem}</Notice>
      ) : (
        <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
          {"Endpoints are asked for the tools that are switched on. Resistivity is not " +
            "among them: it follows the dual-water equation over porosity and saturation, " +
            "and reads no mineral endpoint at all."}
        </span>
      )}
      {model.components.some((component) => !component.role) && (
        <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
          {"This model left the role of some components unstated, so the solver has been " +
            "reading it from their names. The roles shown here are that same reading, and " +
            "publishing writes them down — after which nothing is guessed."}
        </span>
      )}
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"Publishing writes a new version of the model. Every solve already run keeps " +
          "pointing at the version it used, so an older result still says what it was " +
          "computed from."}
      </span>
    </Modal>
  );
}
